import styled from "@emotion/styled";
import { useAtomValue } from "jotai";
import type { ForwardedRef, HTMLAttributes, MouseEventHandler, ReactNode } from "react";
import { useCallback } from "react";

import { rgba } from "@skilitics/designer-theme";
import { Icon, IconButton, SkiliticsIcon, Tooltip, transientOptions } from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { isCanvasContainerVisible } from "../canvas";
import { useNodePickingActive } from "../picking";
import { LayerTitle } from "./LayerTitle";

const LayerItemInner = styled.div<{ $selected?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1 1 100%;
  padding: 6px 0;
  min-width: 0;

  .fa-icon-wrapper {
    color: ${({ $selected, theme }) => ($selected ? theme.primary : theme.generalTextIconVariant)};
  }
`;

const ExpandButton = styled(IconButton, transientOptions)<{ $visible?: boolean }>`
  font-size: 10px;
  visibility: ${({ $visible }) => ($visible ? "visible" : "hidden")};
  width: 14px;
  height: 14px;
  margin-right: 2px;
  color: inherit;
`;

const LayerItemContainer = styled.div<{
  $selected?: boolean;
  $isOverlay?: boolean;
  $isActive?: boolean;
  $nodePickingActive?: boolean;
}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 4px 0 0;
  background: ${({ $selected, theme }) => ($selected ? theme.primaryContainer : "transparent")};
  color: ${({ $selected, theme }) => ($selected ? theme.primary : "inherit")};
  border: solid 1px transparent;
  border-bottom-color: ${({ theme }) => rgba(theme.outlineSoft, 0.2)};

  cursor: ${({ $nodePickingActive }) => ($nodePickingActive ? "crosshair" : "move")};

  ${({ $isActive, theme }) =>
    $isActive &&
    `
    border-color: ${theme.primary};
  `};

  ${({ $isOverlay, theme }) =>
    $isOverlay &&
    `
    background: ${theme.surface};
    box-shadow: ${theme.boxShadow.interacting};
    opacity: 0.5;
  `};
`;

const Hide = styled(IconButton, transientOptions)<{ $isHidden?: boolean; $hovered?: boolean }>`
  visibility: ${({ $hovered, $isHidden }) => ($hovered || $isHidden ? "visible" : "hidden")};

  width: 14px;
  height: 14px;

  font-size: 10px;
  padding: 0;

  svg {
    opacity: ${({ $isHidden }) => ($isHidden ? 0.8 : 1)};
  }
`;

const Lock = styled(IconButton, transientOptions)<{ $isLocked?: boolean; $hovered?: boolean }>`
  visibility: ${({ $hovered, $isLocked }) => ($hovered || $isLocked ? "visible" : "hidden")};

  width: 14px;
  height: 14px;

  font-size: 10px;
  padding: 0;
  margin-right: 4px;

  svg {
    opacity: ${({ $isLocked }) => ($isLocked ? 0.8 : 1)};
  }
`;

const ResponsiveHidden = styled.div`
  width: 14px;
  height: 14px;

  font-size: 10px;
  padding: 0;

  cursor: help;

  svg {
    color: ${({ theme }) => theme.secondarySelection};
  }
`;

type Props = HTMLAttributes<HTMLDivElement> & {
  active?: boolean;
  depth: number;
  expanded?: boolean;
  hasChildren?: boolean;
  hidden?: boolean;
  hiddenReason?: string;
  hovered?: boolean;
  icon?: ReactNode;
  isOverlay?: boolean;
  locked?: boolean;
  selected?: boolean;
  itemId?: number;
  ref?: ForwardedRef<HTMLDivElement>;

  onSelected?: () => void;
  onTitleChange?: (newTitle: string) => void;
  toggleExpanded?: () => void;
  toggleHidden?: () => void;
  toggleLocked?: () => void;
};

export const LayerItem = ({
  active,
  depth,
  expanded,
  hasChildren,
  hidden,
  hiddenReason,
  hovered,
  icon,
  isOverlay,
  locked,
  onSelected,
  onTitleChange,
  selected,
  itemId = 0,
  title,
  toggleExpanded,
  toggleHidden,
  toggleLocked,
  ref,
  ...domAttrs
}: Props) => {
  const formatMessage = useFormatMessage(formatStrings);

  const nodePickingActive = useNodePickingActive();

  const isInViewport = useAtomValue(isCanvasContainerVisible(itemId));

  const handleItemClick: MouseEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      e.stopPropagation();
      if (!selected) {
        onSelected?.();
      }
    },
    [onSelected, selected],
  );

  const handleHideClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      e.stopPropagation();
      toggleHidden?.();
    },
    [toggleHidden],
  );

  const handleLockClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      e.stopPropagation();
      toggleLocked?.();
    },
    [toggleLocked],
  );

  return (
    <LayerItemContainer
      $selected={selected}
      $isOverlay={isOverlay}
      $isActive={active}
      $nodePickingActive={nodePickingActive}
      ref={ref}
      {...domAttrs}
      style={{ ...domAttrs.style, paddingLeft: depth * 10 }}
    >
      <ExpandButton $visible={hasChildren} size="small" onClick={toggleExpanded} data-test="toggle-layer-button">
        <Icon icon={expanded ? "caret-down" : "caret-right"} faPrefix="fas" />
      </ExpandButton>

      <LayerItemInner $selected={selected} onClick={handleItemClick}>
        <LayerTitle
          title={title ?? ""}
          icon={icon}
          selected={selected}
          disabled={nodePickingActive}
          onTitleChange={onTitleChange}
        />

        <Lock $isLocked={locked} $hovered={hovered} onClick={handleLockClick} data-test="toggle-layer-lock-button">
          <SkiliticsIcon icon={locked ? "lock" : "unlock"} size={14} />
        </Lock>

        {!isInViewport && !hidden ? (
          <Tooltip title={hiddenReason ?? formatMessage("hidden")} placement="right">
            <ResponsiveHidden>
              <Icon icon="eye-slash" />
            </ResponsiveHidden>
          </Tooltip>
        ) : (
          <Hide
            $isHidden={hidden}
            $hovered={hovered}
            onClick={handleHideClick}
            data-test="toggle-layer-visibility-button"
          >
            <Icon icon={hidden ? "eye-slash" : "eye"} />
          </Hide>
        )}
      </LayerItemInner>
    </LayerItemContainer>
  );
};

const formatStrings = {
  hidden: {
    en: "Hidden by visibility conditions, or clipped by overflow settings.",
  },
} satisfies FormatMessages;
